import { useFormik } from 'formik';
import type { TFunction } from 'i18next';

import { useFirebaseAuth } from '../../../hooks/useFirebaseAuth';
import { logAuth } from '../../../utils/auth/auth-debug-logger';
import { handleAuthError } from '../../../utils/auth/auth-errors';
import { markSignInPageBackendSignInFlow } from '../../../utils/auth/sign-in-page-backend-flow-session';
import { getSignInFormValidation } from './FormikData';

export function useSignInPage(t: TFunction, afterSignIn: () => void) {
  const {
    logInWithPassword,
    signInWithGoogle,
    signInWithApple,
    signInWithFacebook,
  } = useFirebaseAuth();

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getSignInFormValidation(t),
    initialValues: { email: '', password: '' },
    onSubmit,
  });

  async function onSubmit(values: { email: string; password: string }) {
    try {
      logAuth('signInPage:password:start', { email: values.email });
      markSignInPageBackendSignInFlow();
      await logInWithPassword({
        email: values.email,
        password: values.password,
      });
      logAuth('signInPage:password:firebaseSuccess');
      afterSignIn();
      logAuth('signInPage:password:afterSignInNavigate');
    } catch (error: unknown) {
      console.error('Sign in error: ', error);
      logAuth('signInPage:password:error', {
        message: error instanceof Error ? error.message : String(error),
      });
      handleAuthError(error, t);
    }
  }

  async function signInWithGoogleHandler() {
    try {
      logAuth('signInPage:google:start');
      markSignInPageBackendSignInFlow();
      await signInWithGoogle();
      logAuth('signInPage:google:firebaseSuccess');
      afterSignIn();
      logAuth('signInPage:google:afterSignInNavigate');
    } catch (error: unknown) {
      console.error('Login with Google error: ', error);
      logAuth('signInPage:google:error', {
        message: error instanceof Error ? error.message : String(error),
      });
      handleAuthError(error, t);
    }
  }

  async function signInWithAppleHandler() {
    try {
      logAuth('signInPage:apple:start');
      markSignInPageBackendSignInFlow();
      await signInWithApple();
      logAuth('signInPage:apple:firebaseSuccess');
      afterSignIn();
      logAuth('signInPage:apple:afterSignInNavigate');
    } catch (error: unknown) {
      console.error('Login with Apple error: ', error);
      logAuth('signInPage:apple:error', {
        message: error instanceof Error ? error.message : String(error),
      });
      handleAuthError(error, t);
    }
  }

  async function signInWithFacebookHandler() {
    try {
      logAuth('signInPage:facebook:start');
      markSignInPageBackendSignInFlow();
      await signInWithFacebook();
      logAuth('signInPage:facebook:firebaseSuccess');
      afterSignIn();
      logAuth('signInPage:facebook:afterSignInNavigate');
    } catch (error: unknown) {
      console.error('Login with Facebook error: ', error);
      logAuth('signInPage:facebook:error', {
        message: error instanceof Error ? error.message : String(error),
      });
      handleAuthError(error, t);
    }
  }

  return {
    formik,
    signInWithGoogle: signInWithGoogleHandler,
    signInWithApple: signInWithAppleHandler,
    signInWithFacebook: signInWithFacebookHandler,
  };
}
